import React, {useMemo} from "react";
import {connect} from 'react-redux';
import {compose} from 'redux';
import {translate} from 'react-i18next';
import {Button, Card, Col, Input, Row} from "antd";
import {
    GUTTER_MD,
    MARGIN_MD,
    MODAL_WIDTH_XL,
    PADDING_MD,
    SPAN_ONE_THIRD,
    SPAN_TWO_THIRDS
} from "../../../styles/constants";
import {getLocalizedStr} from "../../../middlewares/i18n/utils";
import {
    changeDataflowPublishingListDataflowDownloadColumns,
    checkDataflowPublishingListDataflowDownloadColumns,
    hideDataflowPublishingListDataflowDownloadQuery,
    setDataflowPublishingListDataflowDownloadQuery,
    showDataflowPublishingListDataflowDownloadPreview,
    unsetDataflowPublishingListDataflowDownloadQuery
} from "./actions";
import _ from "lodash";
import EnhancedModal from "../../../components/enhanced-modal";
import DraggableCheckList from "../../../components/draggable-check-list";
import {DataLanguageConsumer} from "../../../contexts/DataLanguage";
import {QUERY_FORM_FILTER_MODE_TREE} from "../../../components/query-form";
import {
    DOWNLOAD_FORMAT_TYPE_COMPACT,
    DOWNLOAD_FORMAT_TYPE_GENERIC,
    DOWNLOAD_FORMAT_TYPE_GENERIC20,
    DOWNLOAD_FORMAT_TYPE_CROSSSECTIONAL20,
    DOWNLOAD_FORMAT_TYPE_JSON,
    DOWNLOAD_FORMAT_TYPE_SDMXCSV,
    DOWNLOAD_FORMAT_TYPE_STRUCTURE_SPECIFIC
} from "../../../constants/download";
import QueryDownloadFilters from "./QueryDownloadFilters";

const mapStateToProps = state => ({
    appLanguage: state.app.language,
    dataLanguages: state.config.dataManagement.dataLanguages,
    dataflows: state.scenes.mappingHandler.dataflowPublishing.dataflows,
    dlDataflowTriplet: state.scenes.mappingHandler.dataflowPublishing.dlDataflowTriplet,
    dlIsDataflowQueryVisible: state.scenes.mappingHandler.dataflowPublishing.dlIsDataflowQueryVisible,
    dlDsdDimensions: state.scenes.mappingHandler.dataflowPublishing.dlDsdDimensions,
    dlDataflowParams: state.scenes.mappingHandler.dataflowPublishing.dlDataflowParams,
    dlCols: state.scenes.mappingHandler.dataflowPublishing.dlCols,
    dlCheckedCols: state.scenes.mappingHandler.dataflowPublishing.dlCheckedCols,
    filter: state.scenes.mappingHandler.dataflowPublishing.filter,
    treeFilter: state.scenes.mappingHandler.dataflowPublishing.treeFilter,
    filterMode: state.scenes.mappingHandler.dataflowPublishing.filterMode,
});

const mapDispatchToProps = dispatch => ({
    onDownloadQueryHide: () => dispatch(hideDataflowPublishingListDataflowDownloadQuery()),
    onDownloadQuerySet: (cols, filter) => dispatch(setDataflowPublishingListDataflowDownloadQuery(cols, filter)),
    onDownloadQueryUnset: () => dispatch(unsetDataflowPublishingListDataflowDownloadQuery()),
    onPreviewShow: () => dispatch(showDataflowPublishingListDataflowDownloadPreview()),
    onColumnsCheck: cols => dispatch(checkDataflowPublishingListDataflowDownloadColumns(cols)),
    onColumnsChange: cols => dispatch(changeDataflowPublishingListDataflowDownloadColumns(cols)),
});

const cardStyle = {
    type: 'inner',
    headStyle: {
        paddingLeft: PADDING_MD,
        paddingRight: PADDING_MD
    },
    bodyStyle: {
        paddingLeft: PADDING_MD,
        paddingRight: PADDING_MD,
        height: 480,
        overflow: 'auto'
    }
};

const onDrop = (ev, cols) => {
    const array = _.cloneDeep(cols);
    const pos = ev.node.props.pos.split('-');
    const dropPos = ev.dropPosition - Number(pos[pos.length - 1]);

    const from = array.indexOf(ev.dragNode.props.eventKey);
    let to = array.indexOf(ev.node.props.eventKey);
    to = from < to
        ? dropPos < 0 // moving forward into the array
            ? to - 1 // dropPos = -1 => drag on top node gap
            : to // dropPos = 1 => drag on bottom node gap
        : dropPos < 0 // moving backwards into the array
            ? to // dropPos = -1 => drag on top node gap
            : to + 1; // dropPos = 1 => drag on bottom node gap
    if (to === -1) {
        to = 0
    }
    if (to === array.length + 1) {
        to = array.length
    }

    array.splice(to, 0, array.splice(from, 1)[0]);

    return array
};

const findIcon = (label, allColumns) => {
    const col = allColumns.find(item => item.id === label);
    if (!col) {
        return "";
    }
    return col.icon;
};

const DataflowDownloadQueryForm = ({
                                       t,
                                       appLanguage,
                                       dataLanguages,
                                       dlIsDataflowQueryVisible,
                                       dlDataflowTriplet,
                                       dataflows,
                                       dlDataflowParams,
                                       dlDsdDimensions,
                                       filter,
                                       treeFilter,
                                       filterMode,
                                       dlCols,
                                       dlCheckedCols,
                                       onDownloadQueryHide,
                                       onDownloadQuerySet,
                                       onDownloadQueryUnset,
                                       onColumnsCheck,
                                       onColumnsChange,
                                   }) => {
    const dlDataflow = useMemo(() => {
        if (!dataflows || !dataflows.length || !dlDataflowTriplet) {
            return null
        }
        return dataflows.find(d => d.id === dlDataflowTriplet.id && d.agencyID === dlDataflowTriplet.agencyID && d.version === dlDataflowTriplet.version);
    }, [dataflows, dlDataflowTriplet]);

    const isSdmxFilters = useMemo(() => dlDataflowParams && (
        dlDataflowParams.format === DOWNLOAD_FORMAT_TYPE_GENERIC ||
        dlDataflowParams.format === DOWNLOAD_FORMAT_TYPE_STRUCTURE_SPECIFIC ||
        dlDataflowParams.format === DOWNLOAD_FORMAT_TYPE_GENERIC20 ||
        dlDataflowParams.format === DOWNLOAD_FORMAT_TYPE_CROSSSECTIONAL20 ||
        dlDataflowParams.format === DOWNLOAD_FORMAT_TYPE_COMPACT ||
        dlDataflowParams.format === DOWNLOAD_FORMAT_TYPE_SDMXCSV ||
        dlDataflowParams.format === DOWNLOAD_FORMAT_TYPE_JSON
    ), [dlDataflowParams]);

    return (
        <EnhancedModal
            visible={dlIsDataflowQueryVisible}
            width={MODAL_WIDTH_XL}
            title={t('scenes.dataManager.dataflowBuilder.dataflowDownload.modals.query.title')}
            onCancel={onDownloadQueryHide}
            footer={
                <div>
                    <Button onClick={onDownloadQueryHide}>
                        {t('commons.buttons.close.title')}
                    </Button>
                    <Button onClick={onDownloadQueryUnset}>
                        {t('commons.buttons.unset.title')}
                    </Button>
                    <Button
                        type="primary"
                        onClick={() => onDownloadQuerySet(dlCheckedCols, filterMode === QUERY_FORM_FILTER_MODE_TREE ? treeFilter : filter)}
                        disabled={dlCheckedCols && dlCheckedCols.length === 0}
                    >
                        {t('commons.buttons.set.title')}
                    </Button>
                </div>
            }
            withDataLanguageSelector
        >
            <DataLanguageConsumer>
                {dataLanguage => {
                    const lang = dataLanguage || appLanguage;
                    return (
                        <Row gutter={GUTTER_MD}>
                            <Col span={SPAN_ONE_THIRD}>
                                <Card
                                    title={t('scenes.dataManager.dataflowBuilder.dataflowDownload.modals.query.cards.columns.title')}
                                    {...cardStyle}
                                >
                                    <Input
                                        disabled
                                        value={
                                            !!dlDataflow
                                                ? `[${dlDataflow.id}] ${getLocalizedStr(dlDataflow.names, lang, dataLanguages)}`
                                                : null
                                        }
                                    />
                                    <Card
                                        type="inner"
                                        bodyStyle={{height: 391, overflow: "auto"}}
                                        style={{marginTop: MARGIN_MD}}
                                    >
                                        {
                                            dlCols && dlCheckedCols &&
                                            <div className="draggable-check-list-drag-over">
                                                <DraggableCheckList
                                                    items={dlCols}
                                                    getItemLabel={item => item}
                                                    getItemIcon={item => findIcon(item, dlDsdDimensions)}
                                                    getItemKey={item => item}
                                                    checkedKeys={dlCheckedCols}
                                                    onCheck={onColumnsCheck}
                                                    onDrop={ev => onColumnsChange(onDrop(ev, dlCols))}
                                                    disabled={isSdmxFilters}
                                                    leaveNameUnchanged
                                                />
                                            </div>
                                        }
                                    </Card>
                                </Card>
                            </Col>
                            <Col span={SPAN_TWO_THIRDS}>
                                <Card
                                    title={t('scenes.dataManager.dataflowBuilder.dataflowDownload.modals.query.cards.filter.title')}
                                    {...cardStyle}
                                >
                                    <QueryDownloadFilters objectColumns={dlDsdDimensions} objectId={dlDataflowTriplet ? dlDataflowTriplet.id : ""} />
                                </Card>
                            </Col>
                        </Row>
                    )
                }}
            </DataLanguageConsumer>
        </EnhancedModal>
    )
};

export default compose(
    translate(),
    connect(mapStateToProps, mapDispatchToProps)
)(DataflowDownloadQueryForm);
